import React, { useContext, useEffect, useState } from 'react'
import {
  Box, Grid, Link, Typography,
} from '@material-ui/core'
import classes from './BannerWithLogo.styles'
import { cx } from 'linaria'
import { PremiseContext } from '../PremiseTracker/PremiseContext'
import formatAddress from '../../utils/formatAddress'
import BroadbandTabs, {
  BroadbandTabsProps,
} from '../BroadbandTabs/BroadbandTabs'
import BannerWithIcon, {
  BannerWithIconProps,
} from '../BannerWithIcon/BannerWithIcon'
import { useRouter } from 'next/router'
import checkCircle from '../../../public/check-circle.svg'
import flashIcon from '../../../public/flash-sale-icon.svg'
import marketingEvents from '../../utils/marketing/marketingEvents'
import { useGeneralContext } from '../GeneralContext/GeneralContext'
import { useOrderContext } from '../NewOrderFlow/OrderContext'
import { isPremiseLevelPricingDateValid } from '../../utils/premiseLevelPricingUtils'
export interface Button {
  text: string;
  url: string;
  modal: boolean;
}

export interface BannerWithLogoProps {
  readonly meta: {
    id: number;
  };
  title: string;
  body?: string;
  extra_height?: boolean;
  address?: boolean;
  button?: Button;
  tabs?: BroadbandTabsProps;
  banner_with_icon?: BannerWithIconProps;
  social_proof_boroughs?: string;
  social_proof_keyword?: string;
  componentType?: 'div' | 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'h4' ;
}

export default function BannerWithLogo({
  meta,
  title,
  body,
  extra_height,
  address,
  button,
  tabs,
  banner_with_icon,
  social_proof_boroughs,
  social_proof_keyword,
  componentType,
}: BannerWithLogoProps) {
  const router = useRouter()
  const bannerStyling = extra_height ? cx(classes.container, classes.biggerContainer) : classes.container
  const { premise } = useContext(PremiseContext)
  const formattedAddress = premise ? formatAddress(premise.address) : null

  const {
    generalOptions,
    setGeneralOptions,
  } = useGeneralContext()

  // State to track if we're checking for exclusive offers
  const [isCheckingOffers, setIsCheckingOffers] = useState(false)

  const { premiseLevelPricingOffer, premiseLevelPricingStartDate, premiseLevelPricingEndDate, sprn } = generalOptions

  const {
    options,
  } = useOrderContext()

  const { closedBannerWithIcon } = generalOptions
  
  // Check if the premise level pricing offer is valid based on dates
  const isPremiseLevelPricingValid = (offer?: string, startDate?: string, endDate?: string) => {
    // Check if offer is undefined, null, empty string, or just whitespace
    const shouldExcludeAffiliates = options.utmSource === 'awin' && generalOptions.generalSettingPage?.exclude_aff_from_plp === true
    if (!offer || offer.trim() === '' || shouldExcludeAffiliates) {
      return false
    }
    
    return isPremiseLevelPricingDateValid(startDate, endDate)
  }
  
  // Calculate if the premise level pricing offer is valid based on the data in generalOptions
  const hasValidPremiseLevelPricing = React.useMemo(() => {
    return isPremiseLevelPricingValid(
      premiseLevelPricingOffer,
      premiseLevelPricingStartDate,
      premiseLevelPricingEndDate
    )
  }, [premiseLevelPricingOffer, premiseLevelPricingStartDate, premiseLevelPricingEndDate])
  
  // Check if we need to fetch premise level pricing data
  useEffect(() => {
    if (sprn) {
      setIsCheckingOffers(true)
    } else {
      setIsCheckingOffers(false)
    }
  }, [sprn])
  
  // Set checking offers to false when we have premise level pricing data
  useEffect(() => {
    // If we have a response (even if it's null or undefined), stop checking
    if (premiseLevelPricingOffer !== undefined) {
      setIsCheckingOffers(false)
    }
  }, [premiseLevelPricingOffer, premiseLevelPricingStartDate, premiseLevelPricingEndDate, hasValidPremiseLevelPricing])
  
  // Ensure we stop checking after a reasonable timeout even if no response
  useEffect(() => {
    if (isCheckingOffers) {
      const timeoutId = setTimeout(() => {
        setIsCheckingOffers(false)
      }, 2000) // 2 seconds timeout
      
      return () => clearTimeout(timeoutId)
    }
    return undefined // Explicitly return undefined for the false case
  }, [isCheckingOffers])
  
  useEffect(() => {
    if (hasValidPremiseLevelPricing && sprn) {
      marketingEvents.simpleEventWithLimitedParams('PLP_offer_displayed', options, sprn!, premiseLevelPricingOffer!)
    }
  }, [hasValidPremiseLevelPricing])

  // Apply the banner colour based on whether premise level pricing is valid
  const bannerColor = hasValidPremiseLevelPricing 
    ? classes.containerBackgroundOrange 
    : classes.containerBackgroundGrey

  function setClosedBannerWithIcon(closedBannerWithIcon: boolean) {
    setGeneralOptions({
      ...generalOptions,
      closedBannerWithIcon,
    })
  }

  const handleClose = () => {
    setClosedBannerWithIcon(true)
    marketingEvents.closedSocialProofBanner()
  }

  let formattedBody = banner_with_icon?.body

  if (social_proof_boroughs && social_proof_keyword) {
    const socialProofBoroughs = social_proof_boroughs
      .split(/\r?\n/)
      .map((a) => a)
    const currentBorough = socialProofBoroughs.filter((e) => premise?.borough ? e.includes(premise?.borough) : null)[0]
    if (currentBorough) {
      const currentBoroughAmount = currentBorough.split(' | ')[1]
      formattedBody = banner_with_icon?.body.replace(social_proof_keyword, currentBoroughAmount)
    } else {
      formattedBody = null
    }
  }

  const renderExclusiveOffer = () => (
    <Box display='flex' flexDirection="row" alignItems="center" justifyContent="center">
      <Box>
        <Typography component="p" variant='h1' color='primary'>
          <img src={checkCircle} className={classes.icon} alt="check circle" />
          Great news!
        </Typography>
      </Box>
      <Box ml={1} className={classes.exclusiveOffer}>
        <Typography component="p" variant='h6'>
          <img src={flashIcon} alt="flash sale icon" />
          An exclusive offer is available at
        </Typography>
      </Box>
    </Box>
  )


  return (
    <Box component="section" className={cx(bannerStyling, bannerColor)} id={`BannerWithLogo_${meta.id}`}>
      {isCheckingOffers ? (
        // Show nothing while checking for offers
        <Box className={classes.content} alignItems="center" justifyContent="center" flexDirection="column" display="flex">
          {/* Empty loading state */}
        </Box>
      ) : (
        // Show content once we have checked for offers
        <Box className={classes.content} alignItems="center" justifyContent="center" flexDirection="column" display="flex">
          {title && title !== ' ' && <Typography variant="h2">{title}</Typography>}
          <Grid container alignItems="center" justify="center">
            <Box my={2} className={classes.address}>
              {hasValidPremiseLevelPricing ? (
                renderExclusiveOffer()
              ) : (
                <Box mb={1}>
                  <Typography component={componentType ? componentType : 'p'} variant="h1">
                    <img
                      src={checkCircle}
                      className={classes.icon}
                      alt="check circle"
                    />{body ? body : 'Great news! We are available at'}
                  </Typography>
                </Box>
              )}
              {address && formattedAddress && (
                <address>
                  <Typography variant="body2" component="div">
                    {formattedAddress}
                    {'. '}
                    {button &&
                    <Link
                      onClick={() => {
                        router.push(`${router.asPath}${button?.url}`, undefined, {
                          shallow: true,
                        })
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Typography variant="body2">
                        {button?.text}
                      </Typography>
                    </Link>}
                  </Typography>
                </address>)}
            </Box>
          </Grid>
          {formattedBody && banner_with_icon?.body && !closedBannerWithIcon && (
            <BannerWithIcon
              {...banner_with_icon}
              body={formattedBody}
              closedBannerWithIcon={closedBannerWithIcon}
              handleClose={handleClose}
            />
          )}
        </Box>
      )}
      {!isCheckingOffers && tabs && Object.values(tabs).length > 0 && (
        <Box pt={3} className={cx(closedBannerWithIcon && classes.tabsWrapper)}>
          <BroadbandTabs
            broadband_icon={tabs?.broadband_icon}
            broadband_tab={tabs?.broadband_tab}
            bundles_tab={tabs?.bundles_tab}
            bundles_icon={tabs?.bundles_icon}
          />
        </Box>)}
    </Box>
  )
}
